/**
 * @file src/core/RouteTitle.jsx
 *
 * 🏷️ THE TAB SAYS WHERE YOU ARE.
 *
 * Every route rendered the same `document.title` — "Moksy — Turn your idea
 * into a build-ready plan" — on the home page, /login, /welcome, /projects and
 * a project record alike (walked 2026-09-08). Three people pay for that:
 *
 *   1. Screen-reader users, who are read the page title on every navigation.
 *      In a single-page app that is the main signal that navigation happened
 *      at all, so the app announces the marketing tagline and sounds as though
 *      it never moves.
 *   2. Anyone with two tabs open — a customer comparing two projects sees two
 *      identical tabs.
 *   3. History and bookmarks, which become a list of one repeated string.
 *
 * Fixed in ONE place rather than by editing pages: routes are declared as
 * `{ path, element }` with no title, and there are several hundred of them, so
 * a hand-written map would be stale within a week. The name is DERIVED from
 * the path; the table below only overrides the paths where a derived name
 * would be poor.
 *
 * A page that wants a better title still wins: `usePageMeta` runs on mount,
 * after this effect, and restores the previous value on unmount — at which
 * point this sets the new route's title anyway.
 *
 * It always sets something. The first version of this left the title alone
 * when nothing matched, which meant an unlisted route kept the PREVIOUS page's
 * name — logging in landed on /reviewer still titled "Log in" (walked
 * 2026-09-09). A stale title is the confusion this file exists to remove.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SUFFIX = 'Moksy';
const FALLBACK = 'Moksy';

// Only where a derived name would be wrong or graceless. Everything else is
// derived, so a new route is named the day it ships without touching this.
const OVERRIDES = [
  // Derivation gives "Login"/"Register"; the product says "Log in" and
  // "Create an account", and the tab should use the product's words.
  ['/login', 'Log in'],
  ['/register', 'Create an account'],
  ['/requirements/v2/:id', 'Your Briefing'],
  ['/requirements/v2/:id/report', 'Your Blueprint'],
  ['/requirements/:id', 'Your Briefing'],
  ['/projects', 'Your projects'],
  ['/p/:slug', 'Shared Blueprint'],
  ['/invite/:token', 'Your invitation'],
  ['/downloads', 'Exports'],
  ['/downloads/history', 'Exports'],
  ['/concierge/my-requests', 'Sprints'],
  ['/welcome', 'Welcome'],
  ['/profile', 'Your profile']
];

// Segments that describe the console's plumbing rather than the page.
const NOISE = new Set(['control', 'v2', 'v1', 'pages', 'index']);

/**
 * Routes whose title belongs to the BRAND, not to the route: the marketing
 * root, where the tagline is the title and the dictionary owns it. Meta.jsx
 * asks this before writing, so the two never fight over the same tab.
 */
// The brand title Meta last wrote, so a client-side navigation BACK to the
// marketing root can restore it. Without this, RouteTitle returned early on
// the root and left whatever the previous page had set: an admin-only route
// redirecting a member to "/" kept the tab reading "Templates · Moksy" while
// the page said that area is staff-only (walked 2026-09-10) — the same stale
// title this file exists to remove, reintroduced at the one route it skips.
let brandTitle = null;
export const rememberBrandTitle = (title) => { if (title) brandTitle = String(title); };

export const BRAND_TITLE_ROUTE = (pathname = '/') => {
  const p = String(pathname || '/').replace(/\/+$/, '') || '/';
  return p === '/';
};

/**
 * A path segment that is an id rather than a name.
 *
 * The third pattern only knows BARE hex, and half the ids in this product
 * carry a type prefix — `newId('dsp')` gives `dsp-0cccec34-76ae-…`, and
 * review jobs are `rj-…`. Those slipped through and were title-cased into
 * words, so the dispute page's tab read
 *
 *   Dispute · Rj 3fdfd476 B06f 4af3 Afe3 80ec246c0d32 · Moksy
 *
 * which is worse than showing the id, because it pretends to be language
 * (walked 2026-09-09 as the customer raising a dispute).
 *
 * The prefix rule is deliberately tight — a short alphabetic prefix, then at
 * least eight characters of hex and dashes — so real slugs are untouched:
 * "data-model" and "add-face" are names, not ids.
 */
const isId = (seg) => /^[0-9a-f]{8,}$/i.test(seg)
  || /^\d+$/.test(seg)
  || /^[0-9a-f-]{20,}$/i.test(seg)
  // …and the separator is `-` OR `_`: report versions are `rv_258bd031-…`,
  // which slipped through a dash-only rule and titled a reviewer's work
  // console "Rv 258bd031 D817 4162 9b3f 06dfa0fc3fbc · Work" (walked
  // 2026-09-10).
  || /^[a-z]{2,6}[-_][0-9a-f][0-9a-f-]{7,}$/i.test(seg);

const titleCase = (seg) => seg
  .replace(/[-_]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/^([a-z])/, (m) => m.toUpperCase());

/**
 * Does `pathname` match `pattern`, where `:x` is one segment?
 *
 * `:id` matches an ID-SHAPED segment only. It used to match any segment, so
 * `/requirements/v2/admin` matched `/requirements/v2/:id` and the admin console
 * was titled "Your Briefing" (walked as a super admin, 2026-09-09). Every named
 * sibling route under a `:id` pattern would have inherited the same wrong name.
 * `:slug` and `:token` stay permissive — a slug is a word by definition.
 */
const matches = (pattern, pathname) => {
  const p = pattern.split('/').filter(Boolean);
  const a = pathname.split('/').filter(Boolean);
  if (p.length !== a.length) return false;
  if (!p.length) return !a.length;
  return p.every((seg, i) => {
    if (!seg.startsWith(':')) return seg.toLowerCase() === a[i].toLowerCase();
    if (!a[i]) return false;
    return seg === ':id' ? isId(a[i]) : true;
  });
};

/**
 * The page name for a path. Never null — an unnamed route gets the brand
 * rather than the last page's name.
 */
export const titleForPath = (pathname = '/') => {
  const path = String(pathname || '/').replace(/\/+$/, '') || '/';
  // The guest mirror of every /requirements route carries the same names.
  const bare = path.replace(/^\/guest(?=\/)/, '') || '/';
  for (const [pattern, name] of OVERRIDES) if (matches(pattern, bare)) return name;

  const parts = bare.split('/').filter(Boolean).filter((s) => !isId(s) && !NOISE.has(s.toLowerCase()));
  if (!parts.length) return FALLBACK;
  // Deepest segment first — "Providers · Admin" reads better than the reverse
  // in a tab strip, where only the first few characters survive.
  const named = parts.slice().reverse().map(titleCase);
  return named.slice(0, 2).join(' · ');
};

export default function RouteTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    // The marketing root's title is the brand's, and the dictionary owns it.
    // Restore what Meta wrote rather than writing a name of our own — and
    // rather than leaving the previous page's, which is how a refused
    // navigation kept the tab on the page it never reached.
    if (BRAND_TITLE_ROUTE(pathname)) {
      if (brandTitle) document.title = brandTitle;
      return;
    }
    const name = titleForPath(pathname);
    document.title = name === FALLBACK ? FALLBACK : `${name} · ${SUFFIX}`;
  }, [pathname]);
  return null;
}
